"use client";

/**
 * Reusable framer-motion animation primitives.
 * Replace basic AOS data-attributes with richer scroll-triggered
 * animations: staggered grids, fade-ups, parallax, and counters.
 *
 * framer-motion@10.x — imports from "framer-motion".
 */

import {
  motion,
  useInView,
  useMotionValue,
  useTransform,
  useSpring,
  type Variants,
} from "framer-motion";
import {
  useRef,
  useEffect,
  useState,
  type ReactNode,
  type ComponentPropsWithoutRef,
} from "react";
import { cn } from "@/lib/utils";

/* ════════════════════════════════════════════
   1. AnimatedSection — scroll-triggered fade-up
   ════════════════════════════════════════════ */

interface AnimatedSectionProps extends ComponentPropsWithoutRef<"section"> {
  children: ReactNode;
  /** Extra delay in seconds */
  delay?: number;
  /** Override the enter direction: "up" | "down" | "left" | "right" */
  direction?: "up" | "down" | "left" | "right";
}

const directionMap = {
  up: { y: 48, x: 0 },
  down: { y: -48, x: 0 },
  left: { x: 60, y: 0 },
  right: { x: -60, y: 0 },
} as const;

export function AnimatedSection({
  children,
  delay = 0,
  direction = "up",
  className,
  ...rest
}: AnimatedSectionProps) {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const d = directionMap[direction];

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, x: d.x, y: d.y }}
      animate={inView ? { opacity: 1, x: 0, y: 0 } : undefined}
      transition={{
        duration: 0.7,
        delay,
        ease: [0.25, 0.4, 0.25, 1],
      }}
      className={className}
      {...rest}
    >
      {children}
    </motion.section>
  );
}

/* ════════════════════════════════════════════
   2. StaggerGrid — children enter one-by-one
   ════════════════════════════════════════════ */

const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08 },
  },
};

const childVariants: Variants = {
  hidden: { opacity: 0, y: 24, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: [0.25, 0.4, 0.25, 1] },
  },
};

interface StaggerGridProps extends ComponentPropsWithoutRef<"div"> {
  children: ReactNode;
}

export function StaggerGrid({ children, className, ...rest }: StaggerGridProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={containerVariants}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/** Wrap each card in this to participate in StaggerGrid */
export function FadeInChild({
  children,
  className,
  ...rest
}: ComponentPropsWithoutRef<"div"> & { children: ReactNode }) {
  return (
    <motion.div variants={childVariants} className={className} {...rest}>
      {children}
    </motion.div>
  );
}

/* ════════════════════════════════════════════
   3. AnimatedCounter — number count-up on scroll
   ════════════════════════════════════════════ */

interface AnimatedCounterProps {
  /** Target numeric value to count to */
  target: number;
  /** Text appended after the number e.g. "+" */
  suffix?: string;
  /** Duration in seconds */
  duration?: number;
  className?: string;
}

export function AnimatedCounter({
  target,
  suffix = "",
  duration = 2,
  className,
}: AnimatedCounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const totalFrames = 60;
    const increment = target / totalFrames;
    let current = 0;
    const interval = setInterval(() => {
      current += increment;
      if (current >= target) {
        setDisplay(target);
        clearInterval(interval);
      } else {
        setDisplay(Math.floor(current));
      }
    }, (duration * 1000) / totalFrames);
    return () => clearInterval(interval);
  }, [inView, target, duration]);

  return (
    <span ref={ref} className={className}>
      {display}
      {suffix}
    </span>
  );
}

/* ════════════════════════════════════════════
   4. ParallaxOrb — decorative orb with scroll parallax
   ════════════════════════════════════════════ */

interface ParallaxOrbProps {
  className?: string;
  /** Parallax speed multiplier (-1 to 1). Negative = moves up on scroll */
  speed?: number;
}

export function ParallaxOrb({ className, speed = -0.15 }: ParallaxOrbProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const windowCenter = window.innerHeight / 2;
      setOffset((rect.top - windowCenter) * speed);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [speed]);

  return (
    <div
      ref={ref}
      className={cn("pointer-events-none absolute", className)}
      style={{ transform: `translateY(${offset}px)` }}
    />
  );
}

/* ════════════════════════════════════════════
   5. FloatingElement — gentle y-axis float
   ════════════════════════════════════════════ */

export function FloatingElement({
  children,
  className,
  duration = 6,
  distance = 12,
}: {
  children: ReactNode;
  className?: string;
  duration?: number;
  distance?: number;
}) {
  return (
    <motion.div
      animate={{ y: [-distance / 2, distance / 2, -distance / 2] }}
      transition={{ duration, repeat: Infinity, ease: "easeInOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ════════════════════════════════════════════
   6. GlowCard — card with animated gradient border on hover
   ════════════════════════════════════════════ */

export function GlowCard({
  children,
  className,
  glowColor = "indigo",
}: {
  children: ReactNode;
  className?: string;
  glowColor?: "indigo" | "emerald" | "pink" | "amber" | "violet";
}) {
  const glowMap = {
    indigo: "group-hover:shadow-indigo-500/15",
    emerald: "group-hover:shadow-emerald-500/15",
    pink: "group-hover:shadow-pink-500/15",
    amber: "group-hover:shadow-amber-500/15",
    violet: "group-hover:shadow-violet-500/15",
  };

  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-slate-200/60 bg-white/80 backdrop-blur-sm shadow-sm",
        "transition-all duration-300 hover:-translate-y-1 hover:shadow-xl",
        glowMap[glowColor],
        className
      )}
    >
      {children}
    </div>
  );
}

/* Re-export for convenience */
export { motion, useInView } from "framer-motion";
